"use client";

/**
 * RouteAnnouncer — announces SPA route changes to assistive technology.
 *
 * App Router's client navigation doesn't fire a full page load, so screen
 * readers stay silent when the user navigates. This component listens to
 * pathname changes and:
 *   1. Renders the new document title in an aria-live="polite" region so
 *      NVDA / VoiceOver / TalkBack speak it.
 *   2. Moves keyboard focus to <main id="main-content"> (the root layout's
 *      semantic landmark) so the new content is the next Tab target.
 *
 * Initial render is skipped — only subsequent navigations announce. This
 * matches the standard "RouteAnnouncer" pattern in Next.js / Remix / React
 * Router apps.
 *
 * Mount once, at the top of the client tree (Providers in our case). It
 * renders nothing visible — only the visually-hidden live region.
 */

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

export default function RouteAnnouncer() {
  const pathname = usePathname();
  const [announcement, setAnnouncement] = useState("");
  // Skip the announcement on the first render — it would echo the initial
  // <title> that the screen reader already announced on page load.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Wait one frame for Next.js to render the new route and update
    // document.title via per-route metadata. Then capture the title and
    // shift focus into the main landmark.
    const handle = requestAnimationFrame(() => {
      const title = document.title || "";
      setAnnouncement(title);

      const main = document.getElementById("main-content");
      if (main) {
        // Without tabIndex=-1 main isn't programmatically focusable. Set it
        // just for the focus call, then remove so the user's Tab order
        // isn't polluted by a non-interactive element.
        const hadTabIndex = main.hasAttribute("tabindex");
        if (!hadTabIndex) main.setAttribute("tabindex", "-1");
        main.focus({ preventScroll: false });
        if (!hadTabIndex) main.removeAttribute("tabindex");
      }
    });

    return () => cancelAnimationFrame(handle);
  }, [pathname]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      // Visually hidden but available to AT.
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        margin: -1,
        padding: 0,
        border: 0,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
      }}
    >
      {announcement}
    </div>
  );
}
